import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styleSheets/selectAttachments.css";
import { Attachment } from "../API";
import { useTranslation } from "react-i18next";

interface SelectAttachmentsProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    userAttachments: Attachment[];
    selectedAttachments: Attachment[];
    onAddAttachment: (attachment: Attachment) => void;
}

const SelectAttachments: React.FC<SelectAttachmentsProps> = ({
                                                                 isOpen,
                                                                 setIsOpen,
                                                                 userAttachments,
                                                                 selectedAttachments,
                                                                 onAddAttachment,
                                                             }) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const previousActiveElementRef = useRef<HTMLElement | null>(null);
    const [search, setSearch] = useState("");
    const { t } = useTranslation("common");

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;

        previousActiveElementRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;

        const frameId = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });

        const onMouseDown = (e: MouseEvent) => {
            const el = wrapRef.current;
            if (!el) return;
            if (!el.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        return () => {
            window.cancelAnimationFrame(frameId);
            document.removeEventListener("mousedown", onMouseDown);

            const previous = previousActiveElementRef.current;
            previousActiveElementRef.current = null;

            if (previous && document.contains(previous)) {
                previous.focus();
            }
        };
    }, [isOpen, setIsOpen]);

    // Reset search when opening/closing
    useEffect(() => {
        if (!isOpen) setSearch("");
    }, [isOpen]);

    const selectedIds = useMemo(
        () => new Set(selectedAttachments.map((a) => a.id)),
        [selectedAttachments]
    );

    const availableAttachments = useMemo(() => {
        const q = search.trim().toLowerCase();

        return userAttachments
            .filter((a) => !selectedIds.has(a.id))
            .filter((a) => {
                if (!q) return true;

                const name = (a.name ?? "").toString().toLowerCase();
                const fileName = (a.s3Key?.split("/").pop() ?? "").toString().toLowerCase();
                return name.includes(q) || fileName.includes(q);
            })
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [userAttachments, selectedIds, search]);

    if (!isOpen) return null;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            setIsOpen(false);
        }
    };

    return (
        <div
            className="attachments-dropdown"
            ref={wrapRef}
            role="region"
            aria-label={t("file.attachments")}
            onKeyDown={handleKeyDown}
        >
            <div className="attachments-dropdown__searchRow">
                <input
                    ref={searchInputRef}
                    className="attachments-dropdown__search"
                    type="text"
                    value={search}
                    placeholder={t("common.searchFiles")}
                    aria-label={t("common.searchFiles")}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="attachments-dropdown__list" aria-label={t("file.attachments")}>
                {availableAttachments.length === 0 ? (
                    <div className="attachments-dropdown__empty">No available attachments</div>
                ) : (
                    availableAttachments.map((a) => {
                        const fileName = a.s3Key?.split("/").pop() ?? "";
                        const label =
                            (a.name ?? "").trim() ||
                            fileName.trim() ||
                            a.s3Key?.toString() ||
                            a.id;

                        const subLabel =
                            (a.name ?? "").trim() && fileName
                                ? `(${fileName})`
                                : "";

                        return (
                            <button
                                type="button"
                                key={a.id}
                                className="attachments-dropdown__item"
                                onClick={() => {
                                    onAddAttachment(a);
                                    setIsOpen(false);
                                }}
                            >
                                <span className="attachments-dropdown__itemTitle">{label}</span>
                                {subLabel && (
                                    <span className="attachments-dropdown__itemMeta">{subLabel}</span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default SelectAttachments;
